import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Form, Input, DatePicker, TimePicker, Select, InputNumber } from "antd";
import CrudButtons from "components/button/crudbuttons";
import FormItem from "components/fields/FormItem";
import dayjs from "dayjs";
import { requiredField } from "utils/crudSagaHelper";
import {
  getEmployeeScheduleDetails,
  doEmployeeScheduleCreate,
  doEmployeeScheduleUpdate,
} from "../../../../../redux/employee/employeeState";
import { DATE_FORMAT, TIME_FORMAT } from "variables/constants";
import { formatDateTimeOrNull } from "utils";
import { MODULE_EMPLOYEES } from "variables/constants";
import { TIME_FORMAT_12HM } from "variables/constants";
import SwitchField from "components/fields/SwitchField";

const EmployeeScheduleForm = ({ 
  onCancel, 
  employeeId,
  submitLabel,
  ...props
}) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { recordId, mode } = props;

  const daysOfWeek = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday",];

  const recordData = useSelector((state) => state.employee.employeeScheduleDetails?.data);

  const doCreate = (data) => dispatch(doEmployeeScheduleCreate(data));
  const doUpdate = (data) => dispatch(doEmployeeScheduleUpdate(data));
  const { RangePicker } = TimePicker;

  useEffect(() => {
    if (recordData ) {
      form.resetFields();
      const { employee_schedule:employeeSchedule, schedules, ...rest } = recordData;
      
      const initialValues = {
        ...rest,
        ...employeeSchedule,
        esd_employee_id: employeeId,
        esd_effective_date_from: recordData?.esd_effective_date_from ? dayjs(recordData.esd_effective_date_from) : null,
        esd_effective_date_to: recordData?.esd_effective_date_to ? dayjs(recordData.esd_effective_date_to) : null,
      schedules: (schedules || []).map(schedule => ({
        ...schedule,
        esd_time_in: schedule.esd_time_in
          ? dayjs(schedule.esd_time_in, TIME_FORMAT)
          : null,
        esd_time_out: schedule.esd_time_out
          ? dayjs(schedule.esd_time_out, TIME_FORMAT)
          : null,
        time_range:
          schedule.esd_time_in && schedule.esd_time_out
            ? [
                dayjs(schedule.esd_time_in, TIME_FORMAT),
                dayjs(schedule.esd_time_out, TIME_FORMAT),
              ]
            : [],

        esd_flexible_time: schedule.esd_flexible_time ?? null,
      })),
      };

      form.setFieldsValue(initialValues);
    }
  }, [recordData, employeeId, form]);

  useEffect(() => {
    dispatch(
      getEmployeeScheduleDetails({
        employeeId,
        scheduleId: recordId,
      })
    );
  }, [dispatch, employeeId, recordId]);

  const onSubmit = (formData) => { 
    console.log(formData);
    try {
      formData.esd_effective_date_from = formatDateTimeOrNull(formData.esd_effective_date_from, DATE_FORMAT);
      formData.esd_effective_date_to = formatDateTimeOrNull(formData.esd_effective_date_to, DATE_FORMAT);
      formData.esd_employee_id = employeeId;
      
      formData.schedules = formData.schedules.map(schedule => ({
        ...schedule,
        esd_employee_id: employeeId,
        esd_effective_date_from: formatDateTimeOrNull(formData.esd_effective_date_from, DATE_FORMAT),
        esd_effective_date_to: formatDateTimeOrNull(formData.esd_effective_date_to, DATE_FORMAT),
        esd_time_in: formatDateTimeOrNull(schedule.esd_time_in, TIME_FORMAT),
        esd_time_out: formatDateTimeOrNull(schedule.esd_time_out, TIME_FORMAT),
        esd_is_flexible: Number(Boolean(schedule.esd_is_flexible)),
      }));
  
      console.log(formData);
      recordData?.esd_id > 0 ? doUpdate(formData) : doCreate(formData);
      
    } catch (error) {
      console.error("Save failed:", error);
    }
  };

  return (
    <Form className={mode !== "view" ? "form" : ""}
      layout="vertical" 
      form={form} 
      onFinish={onSubmit}
    > 
      <FormItem
        name="esd_employee_id"
        hidden
      >
        <Input type="hidden"/>
      </FormItem>
      <FormItem
        name="esd_id"
        hidden
      >
        <Input type="hidden"/>
      </FormItem>
      <div className="mb-0">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-y-2 gap-x-4 mb-2 w-full">
          <FormItem extra="md:col-span-1 md:col-start-2"
              label="Effective Date From"
              name="esd_effective_date_from"
              dependencies={["esd_effective_date_from"]}
              rules={[
                requiredField(),
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const fromDate = getFieldValue("esd_effective_date_from");

                    if (!value || !fromDate) {
                      return Promise.resolve();
                    }

                    if (value.isBefore(fromDate, "day")) {
                      return Promise.reject(
                        new Error("Effective Date To cannot be earlier than From")
                      );
                    }

                    return Promise.resolve();
                  },
                }),
              ]}
          >
            {mode !== "view" ?
              <DatePicker id="esd_effective_date_from"
                className="input-field"
                format={DATE_FORMAT}
                style={{
                  width: "100%",
                }}
              />
            : 
              <label className="text-base label-value dark:text-white">
                {recordData?.esd_effective_date_from}
              </label>
            }
          </FormItem>
          <FormItem
            label={<span className="px-2">Effective Date To</span>}
            name="esd_effective_date_to"
          >
            {mode !== "view" ? (
              <DatePicker
                id="esd_effective_date_to"
                className="input-field"
                format={DATE_FORMAT}
                style={{ width: "100%" }}
                disabledDate={(current) => {
                  const fromDate = form.getFieldValue("esd_effective_date_from");

                  return fromDate && current.isBefore(fromDate, "day");
                }}
              />
            ) : (
              <label className="text-base label-value dark:text-white">
                {recordData?.esd_effective_date_to}
              </label>
            )}
          </FormItem>
        </div>
        <div className="w-full">
          <Form.List name="schedules" initialValue={recordData?.schedules || []}>
            {(fields) =>
              fields.map((field, index) => (
                <div className="grid grid-cols-1 md:grid-cols-7 gap-y-2 gap-x-4 mb-2 border-2 rounded-2xl p-2 w-full" key={`${field.key}`}>
                  <FormItem 
                    {...field}
                    name={[index, "esd_day"]}
                    initialValue={index}
                    hidden
                  >
                    <Input type="hidden" />
                  </FormItem>
                  <FormItem 
                    {...field}
                    name={[index, "esd_id"]}
                    hidden
                  >
                    <Input type="hidden"/>
                  </FormItem>
                  <FormItem extra="md:col-span-2"
                    {...field} 
                    name={[index, "esd_type_id"]}
                    label={daysOfWeek[index] || `Day ${index + 1}`}
                    rules={[requiredField()]}
                  >
                    <Select id="esd_type_id"
                      className="input-field"
                      showSearch
                      allowClear={true}
                      options={recordData?.scheduleTypeOptions}
                    /> 
                  </FormItem>	
                  <FormItem
                    extra="md:col-span-2"
                    label={<span className="px-2">Time In / Time Out</span>}
                  >
                    <RangePicker
                      picker="time"
                      format={TIME_FORMAT_12HM}
                      className="input-field"
                      style={{ width: "100%" }}
                      allowClear={true}
                    />
                  </FormItem>
                    <div className="flex gap-2 md:col-span-3">
                      <FormItem
                        {...field}
                        valuePropName="checked"
                        name={[index, "esd_is_flexible"]}
                        label={<span className="px-2">Is Flexible</span>}
                      >
                        <SwitchField
                          id={`esd_is_flexible_${index}`}
                          type="text"
                          labelYes="Yes"
                          labelNo="No"
                        />
                      </FormItem>

                      <Form.Item
                        noStyle
                        shouldUpdate={(prevValues, currentValues) =>
                          prevValues.schedules?.[index]?.esd_is_flexible !==
                          currentValues.schedules?.[index]?.esd_is_flexible
                        }
                      >
                        {({ getFieldValue }) => {
                          const isFlexible = getFieldValue([
                            "schedules",
                            index,
                            "esd_is_flexible",
                          ]);

                          return isFlexible ? (
                            <FormItem
                              {...field}
                              name={[index, "esd_flexible_time"]}
                              label="Enter Flexible Time"
                            >
                              <InputNumber
                                className="input-field"
                                placeholder="Flexible time"
                                min={1}
                              />
                            </FormItem>
                          ) : null;
                        }}
                      </Form.Item>
                    </div>
                </div>
              ))
            }
          </Form.List>
        </div>
      </div>

      <div className="w-full flex justify-end">
        {(mode === "edit") && (
          <CrudButtons
            mode={mode}
            recordId={recordData?.esd_id ?? 0}
            isModal={true}
            modalId={recordData?.esd_id > 0 ? 'edit_employee_schedule' : 'add_employee_schedule'}
            cancelLabel="Cancel"
            doHideModal={onCancel}
            submitLabel={submitLabel}
            moduleId={MODULE_EMPLOYEES}
          />
        )}
      </div>
    </Form>
  );
};

export default (EmployeeScheduleForm);
